import { useQuery } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { Panel, StateView } from '@/card/record'
import { Icon } from '@/components/Icon'
import { fetchStatementImport, fetchStatementImports } from './api'
import { dateText, Money, QueryFailure, timeText } from './PaymentsShell'
import type { StatementImport } from './types'

/**
 * История загрузок выписки по счёту и одна открытая загрузка.
 *
 * Повторная загрузка того же файла — норма протокола 1С (идентификатора транзакции в нём
 * нет), и следующий вопрос оператора — «когда и кто это уже загружал». Сервер помнит
 * каждую загрузку, повторную тоже, и у повторной называет ту, что принесла строки
 * первой: «Открыть ту загрузку» ведёт сюда, а не к догадке по дате.
 *
 * Открытая загрузка показывает строки, которые принесла именно она. У полностью повторной
 * их нет — это сказано словами и дана ссылка на исходную, чтобы пустая таблица не читалась
 * как «файл был пуст».
 */
export function StatementImports({
  accountId,
  openedId,
  onOpen,
}: {
  accountId: string
  openedId: string | null
  onOpen: (importId: string | null) => void
}) {
  const { t } = useApp()
  const history = useQuery({
    queryKey: ['statement-imports', accountId],
    queryFn: ({ signal }) => fetchStatementImports(accountId, signal),
    retry: false,
  })

  const list = history.data ?? []
  return (
    <Panel title={t('payments.imports.title')} count={history.data ? list.length : null} flush>
      {history.error ? (
        <div className="panel-b"><QueryFailure error={history.error} compact /></div>
      ) : history.isLoading ? (
        <div className="panel-b"><span className="skel" style={{ height: 40 }} aria-busy="true" /></div>
      ) : list.length === 0 ? (
        <div className="panel-b"><StateView compact kind="empty" title={t('payments.imports.empty')} /></div>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl" aria-label={t('payments.imports.title')}>
            <thead>
              <tr>
                <th>{t('payments.imports.col.at')}</th>
                <th>{t('payments.imports.col.file')}</th>
                <th className="num">{t('payments.imports.col.lines')}</th>
                <th className="num">{t('payments.imports.col.new')}</th>
                <th className="num">{t('payments.imports.col.duplicates')}</th>
                <th>{t('payments.imports.col.by')}</th>
                <th><span className="sr-only">{t('payments.recon.col.action')}</span></th>
              </tr>
            </thead>
            <tbody>
              {list.map((upload) => (
                <UploadRow key={upload.id} upload={upload} opened={upload.id === openedId} onOpen={onOpen} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {openedId && <OpenedUpload importId={openedId} onOpen={onOpen} />}
    </Panel>
  )
}

function UploadRow({ upload, opened, onOpen }: {
  upload: StatementImport
  opened: boolean
  onOpen: (importId: string | null) => void
}) {
  const { t } = useApp()
  return (
    <tr aria-selected={opened}>
      <td>{dateText(upload.importedAt)} <span className="muted">{timeText(upload.importedAt)}</span></td>
      <td className="mono">{upload.fileName ?? <span className="muted">{t('payments.imports.noFile')}</span>}</td>
      <td className="num">{upload.linesInFile}</td>
      <td className="num">{upload.imported}</td>
      <td className="num">
        {upload.duplicates}
        {upload.fullyDuplicate && <span className="badge b-warning" style={{ marginLeft: 6 }}>{t('payments.imports.repeat')}</span>}
      </td>
      <td>{upload.importedByName ?? <span className="muted">—</span>}</td>
      <td className="num">
        <button type="button" className="btn btn-ghost btn-sm" aria-pressed={opened}
                onClick={() => onOpen(opened ? null : upload.id)}>
          {opened ? t('payments.imports.close') : t('payments.imports.open')}
        </button>
      </td>
    </tr>
  )
}

function OpenedUpload({ importId, onOpen }: { importId: string; onOpen: (importId: string | null) => void }) {
  const { t } = useApp()
  const upload = useQuery({
    queryKey: ['statement-import', importId],
    queryFn: ({ signal }) => fetchStatementImport(importId, signal),
    retry: false,
  })

  if (upload.error) return <div className="panel-b"><QueryFailure error={upload.error} compact /></div>
  if (!upload.data) return <div className="panel-b"><span className="skel" style={{ height: 40 }} aria-busy="true" /></div>

  const data = upload.data
  const lines = data.lines ?? []
  return (
    <section className="panel-b stack" style={{ gap: 8 }} aria-label={t('payments.imports.opened', { file: data.fileName ?? '—' })}>
      <span className="strong">
        {t('payments.imports.opened', { file: data.fileName ?? t('payments.imports.noFile') })}
      </span>
      <span className="t-sm muted">
        {dateText(data.importedAt)} {timeText(data.importedAt)}
        {data.accountNumber ? ` · …${data.accountNumber.slice(-4)}` : ''}
        {data.importedByName ? ` · ${data.importedByName}` : ''}
        {' · '}
        {t('payments.imports.summary', { lines: data.linesInFile, imported: data.imported, duplicates: data.duplicates })}
      </span>
      {lines.length === 0 ? (
        <div className="alert alert-neutral" role="status">
          <Icon name="inbox" />
          <span>
            {t('payments.imports.noOwnLines')}
            {data.duplicateOfImportId && (
              <>
                {' '}
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => onOpen(data.duplicateOfImportId ?? null)}>
                  {t('payments.import.openOriginal')}
                </button>
              </>
            )}
          </span>
        </div>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl" aria-label={t('payments.imports.lines')}>
            <thead>
              <tr>
                <th>{t('payments.recon.col.date')}</th>
                <th>{t('payments.recon.col.counterparty')}</th>
                <th>{t('payments.recon.col.purpose')}</th>
                <th className="num">{t('payments.recon.col.in')}</th>
                <th className="num">{t('payments.recon.col.out')}</th>
                <th>{t('payments.recon.col.status')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.id}>
                  <td className="muted">{dateText(line.statementDate)}</td>
                  <td>{line.counterpartyName ?? '—'}</td>
                  <td><span className="ellipsis pay-purpose" title={line.purpose ?? undefined}>{line.purpose ?? '—'}</span></td>
                  <td className="num">{line.direction === 'incoming' ? <Money value={line.amount} currency={line.currency} /> : ''}</td>
                  <td className="num">{line.direction === 'outgoing' ? <Money value={line.amount} currency={line.currency} /> : ''}</td>
                  <td>{line.processed ? t('payments.imports.processed') : t('payments.recon.status.line')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
